/**
 * OrderByNullsDropdown — labeled NULLS-placement mini-dropdown for one ORDER
 * BY entry, replacing the old blind 3-state cycle + cryptic em-dash (SEL-13).
 * Displays `NULLS: default` / `NULLS: FIRST` / `NULLS: LAST`; picking an
 * option commits via the SAME `modifyOrderBy` two-commit chain
 * `OrderByItemRow`'s direction toggle already uses. Dialect gating
 * (`showNullsToggle`) stays the caller's responsibility — this only renders
 * once the caller has decided to show it.
 *
 * Split out of `OrderByItemRow.tsx` to keep both files under the project's
 * 200-line budget — mirrors the `JoinLine.tsx` + `JoinKindDropdown.tsx` split.
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove ORDER BY.
 * VQE Polish — Wave 0 primitive #7 (SEL-13).
 */
import { ChevronDown } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import {
  formatOrderByClauseText,
  type OrderByDirection,
  type OrderByNulls,
} from '../../edit/edits/addOrderBy';
import { modifyOrderBy } from '../../edit/edits/modifyOrderBy';
import { VqeTooltip } from '../common/VqeTooltip';

export interface OrderByNullsDropdownProps {
  readonly index: number;
  readonly nulls: 'FIRST' | 'LAST' | null;
  readonly expression: string;
  readonly renderedDirection: OrderByDirection;
  readonly editReady: boolean;
  readonly editContext?: VqeEditContext;
  readonly statementStartOffset: number;
}

const NULLS_NOT_READY_TOOLTIP =
  'Change NULLS placement · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”';

type NullsChoice = 'FIRST' | 'LAST' | null;

const OPTIONS: readonly { readonly value: NullsChoice; readonly label: string; readonly key: string }[] = [
  { value: null, label: 'default', key: 'default' },
  { value: 'FIRST', label: 'FIRST', key: 'first' },
  { value: 'LAST', label: 'LAST', key: 'last' },
];

const TRIGGER_CLASS =
  'inline-flex items-center gap-1 rounded bg-background px-1.5 py-0.5 text-[10px] font-semibold ' +
  'uppercase tracking-wide text-muted-foreground transition-colors';

export function OrderByNullsDropdown({
  index,
  nulls,
  expression,
  renderedDirection,
  editReady,
  editContext,
  statementStartOffset,
}: OrderByNullsDropdownProps) {
  const testId = `vqe-order-by-item-nulls-${index}`;
  const label = nulls ?? 'default';
  // Only the keywords the editor would write (FIRST / LAST) are capitalised;
  // the unset state reads as a plain, muted "default" — the trigger's
  // `uppercase` class would otherwise turn it into SQL-looking "DEFAULT".
  const labelNode =
    nulls === null ? <span className="font-normal normal-case">default</span> : nulls;

  if (!editReady || !editContext) {
    return (
      <VqeTooltip content={NULLS_NOT_READY_TOOLTIP}>
        <button
          type="button"
          disabled
          aria-disabled
          aria-label={`NULLS placement dropdown (currently ${label})`}
          data-testid={testId}
          className={cn(TRIGGER_CLASS, 'disabled:cursor-not-allowed disabled:hover:bg-background')}
        >
          NULLS: {labelNode}
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
      </VqeTooltip>
    );
  }

  const commit = (next: NullsChoice): void => {
    if (next === nulls) return;
    const structure = extractStructureForContext(editContext);
    const nextRendered: OrderByNulls = next === 'FIRST' ? 'first' : next === 'LAST' ? 'last' : null;
    const newClauseText = formatOrderByClauseText(expression, renderedDirection, nextRendered);
    void modifyOrderBy(index, newClauseText, toCommitCtx(editContext), {
      structure,
      statementStartOffset,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label={`NULLS placement dropdown (currently ${label})`}
        data-testid={testId}
        className={cn(TRIGGER_CLASS, 'cursor-pointer hover:bg-accent hover:text-foreground hover:ring-1 hover:ring-ring/50')}
      >
        NULLS: {labelNode}
        <ChevronDown className="h-3 w-3" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" data-testid={`${testId}-menu`}>
        {OPTIONS.map((opt) => (
          <DropdownMenuItem
            key={opt.key}
            data-testid={`${testId}-option-${opt.key}`}
            onSelect={() => commit(opt.value)}
            className={opt.value === nulls ? 'font-semibold' : undefined}
          >
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
